import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  /** Override the favicon for this page only (e.g. Launchpad rocket). */
  favicon?: string;
}

const BASE_URL = 'https://trustroi.online';
const DEFAULT_OG_IMAGE = 'https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/93209633-6cc4-4c74-b981-2b683e26d55d/id-preview-31319292--16b6a7a8-8c87-4ead-82d5-12ca947e8838.lovable.app-1772795064093.png';

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'TrustROI',
  url: BASE_URL,
  logo: `${BASE_URL}/favicon.png`,
  description: 'Software ROI Intelligence Platform — compare tools by payment-gateway-verified data, not reviews or upvotes.',
  sameAs: [
    'https://www.linkedin.com/company/trustroi/',
    'https://x.com/trustroi_online',
    'https://www.reddit.com/r/TrustROI/',
  ],
};

export function SEOHead({ title, description, canonical, ogImage, ogType = 'website', noindex = false, favicon }: SEOHeadProps) {
  const location = useLocation();
  const rawPath = canonical ?? location.pathname;
  // Normalize: lowercase + strip trailing slash (except root) so canonicals
  // are consistent across /Launchpad, /launchpad, /launchpad/ etc.
  const normalize = (p: string) => {
    let out = (p || '/').toLowerCase();
    if (out.length > 1 && out.endsWith('/')) out = out.replace(/\/+$/, '');
    return out;
  };
  const cleanPath = normalize(rawPath);
  const canonicalUrl = `${BASE_URL}${cleanPath === '/' ? '' : cleanPath}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {!noindex && <link rel="canonical" href={canonicalUrl || BASE_URL} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {favicon && <link rel="icon" href={favicon} type="image/png" />}
      {favicon && <link rel="apple-touch-icon" href={favicon} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl || BASE_URL} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="TrustROI" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">
        {JSON.stringify(ORGANIZATION_SCHEMA)}
      </script>
    </Helmet>
  );
}
